import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Slider,
  Stack,
  IconButton,
  Alert,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import JudgePicker from "./JudgePicker";
import { updateFinalNightSetup } from "../../../api/competitionFinalNight";
import { toast } from "react-toastify";

const MAX_ROUNDS = 10;
const gold = "#C9A66B";

function mapRoundsFromApi(apiRounds = []) {
  return [...apiRounds]
    .sort((a, b) => (a.round_order || 0) - (b.round_order || 0))
    .map((r) => ({
      id: r.id,
      name: r.name || "",
      roundStatus: r.round_status || "pending",
      isEnabled: Boolean(r.is_enabled),
      judges: (r.judges || [])
        .map((j) => j.judgeUser || j.user)
        .filter(Boolean)
        .map((u) => ({
          ...u,
          profileImage: u.profile_img || u.profileImage,
        })),
    }));
}

export default function FinalNightSetupPanel({
  competitionId,
  token,
  judgesPercent = 70,
  usersPercent = 30,
  apiRounds = [],
  hasActiveRound = false,
  competitionStopped = false,
  finalNightStarted = false,
  onSaved,
}) {
  const [judges, setJudges] = useState(judgesPercent);
  const [users, setUsers] = useState(usersPercent);
  const [rounds, setRounds] = useState([{ name: "", judges: [] }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setJudges(judgesPercent);
    setUsers(usersPercent);
  }, [judgesPercent, usersPercent]);

  useEffect(() => {
    const mapped = mapRoundsFromApi(apiRounds);
    if (mapped.length) setRounds(mapped);
  }, [apiRounds]);

  const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));

  const handleJudges = (raw) => {
    const j = clamp(raw);
    setJudges(j);
    setUsers(100 - j);
  };

  const addRound = () => {
    if (rounds.length >= MAX_ROUNDS) return;
    setRounds((prev) => [...prev, { name: "", judges: [] }]);
  };

  const removeRound = (index) => {
    if (rounds.length <= 1) return;
    const round = rounds[index];
    if (round?.isEnabled || round?.roundStatus === "completed") return;
    setRounds((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRound = (index, patch) => {
    setRounds((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const isRoundLocked = (round) =>
    Boolean(round?.isEnabled) || round?.roundStatus === "completed";

  const canConfigure = competitionStopped || finalNightStarted;

  const handleSave = async () => {
    if (!canConfigure) {
      toast.error("Stop the competition before configuring Final Night scoring.");
      return;
    }
    for (let i = 0; i < rounds.length; i++) {
      if (!rounds[i].name?.trim()) {
        toast.error(`Round ${i + 1} needs a name`);
        return;
      }
      if (!rounds[i].judges?.length) {
        toast.error(`Round ${i + 1} needs at least one judge`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await updateFinalNightSetup(
        {
          id: Number(competitionId),
          judges_percent: judges,
          users_percent: users,
          rounds: rounds.map((r) => ({
            ...(r.id ? { id: r.id } : {}),
            name: r.name.trim(),
            judgeIds: (r.judges || []).map((j) => j.id),
          })),
        },
        token
      );
      if (res.data.success) {
        toast.success("Final Night scoring setup saved");
        const savedRounds = res.data.data?.rounds;
        if (savedRounds?.length) {
          setRounds(mapRoundsFromApi(savedRounds));
        }
        onSaved?.();
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save setup");
    } finally {
      setSaving(false);
    }
  };

  if (!canConfigure) {
    return (
      <Alert severity="warning">
        Stop the competition from the listing first — then configure voting
        weight and Final Night rounds here.
      </Alert>
    );
  }

  return (
    <Box>
      <SectionTitle
        title="Voting weight"
        subtitle="Judges % + Users % = 100%. Round 1 shows competition votes plus live Final Night votes."
      />
      <Stack direction={{ xs: "column", sm: "row" }} gap={2} sx={{ mb: 2 }}>
        <TextField
          label="Judges %"
          type="number"
          size="small"
          value={judges}
          onChange={(e) => handleJudges(e.target.value)}
          inputProps={{ min: 0, max: 100 }}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Users %"
          type="number"
          size="small"
          value={users}
          onChange={(e) => {
            const u = clamp(e.target.value);
            setUsers(u);
            setJudges(100 - u);
          }}
          inputProps={{ min: 0, max: 100 }}
          sx={{ flex: 1 }}
        />
      </Stack>
      <Slider
        value={judges}
        onChange={(_, v) => handleJudges(v)}
        min={0}
        max={100}
        valueLabelDisplay="auto"
        sx={{ color: gold, mb: 3 }}
      />

      <SectionTitle
        title="Rounds & judges"
        subtitle="Define each round and assign judges. Enable them one at a time below during the live show."
      />
      {hasActiveRound && finalNightStarted ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          A round is live — you can assign judges and add new rounds. Active and
          completed rounds cannot be removed or renamed.
        </Alert>
      ) : null}

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {rounds.map((round, index) => {
          const locked = isRoundLocked(round);
          return (
          <Paper
            key={round.id || `new-${index}`}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, bgcolor: "#fafbfc" }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Typography fontWeight={700}>Round {index + 1}</Typography>
              <IconButton
                size="small"
                onClick={() => removeRound(index)}
                disabled={locked || rounds.length <= 1}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField
              fullWidth
              size="small"
              label="Round name"
              value={round.name}
              disabled={locked}
              onChange={(e) => updateRound(index, { name: e.target.value })}
              sx={{ mb: 1.5 }}
            />
            <JudgePicker
              value={round.judges || []}
              onChange={(judgesList) => updateRound(index, { judges: judgesList })}
            />
          </Paper>
          );
        })}
      </Stack>

      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          disabled={rounds.length >= MAX_ROUNDS}
          onClick={addRound}
          sx={{ borderColor: gold, color: "#9a7b3f", textTransform: "none" }}
        >
          Add round
        </Button>
        <Button
          startIcon={<SaveIcon />}
          variant="contained"
          disabled={saving}
          onClick={handleSave}
          sx={{
            ml: "auto",
            bgcolor: gold,
            textTransform: "none",
            fontWeight: 700,
            "&:hover": { bgcolor: "#b8925a" },
          }}
        >
          {saving ? "Saving…" : "Save scoring setup"}
        </Button>
      </Stack>
    </Box>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography fontWeight={700} sx={{ color: "#9a7b3f" }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography fontSize={13} color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}
